<template>
    <v-dialog v-model="showAddClientDialog" max-width="600" persistent>
        <v-card class="pa-4">
            <div class="text-h6 mb-4">
                {{ isEditingClient ? $t('broker.dialogs.editClient') : $t('broker.dialogs.addNewClient') }}
            </div>

            <v-form>

                <!--email to client whitelabel if partner-->
                <div v-show="userRole === 'partner'">
                    <p class="text-caption ma-0" style="line-height:.2rem">White label URL</p>
                    <v-text-field class="pt-0" disabled :label="whiteLabelUrl">
                        <template v-slot:append-outer>
                            <v-btn disabled small color="primary" @click="">
                                <v-icon left small>mdi-email</v-icon>
                                {{ $t('instructions.emailToClient') }}
                            </v-btn>
                        </template>
                    </v-text-field>
                </div>

                <v-text-field data-testid="client-company-name" v-model="localForm.companyName" :label="$t('broker.labels.companyName')" />
                <v-text-field data-testid="client-representative" v-model="localForm.companyRepresentative" :disabled="isEditingClient"
                    :label="isBroker ? $t('broker.labels.companyRep') : $t('broker.labels.companyContactName')" />
                <v-text-field data-testid="client-cnpj" v-model="localForm.cnpj" :disabled="isEditingClient" :label="$t('broker.labels.cnpj')"
                    placeholder="00.000.000/0000-00" @input="formatCNPJ" @keypress="onlyNumbers" maxlength="18" />
                <v-text-field data-testid="client-email" v-model="localForm.email" :disabled="isEditingClient"
                    :label="$t('broker.labels.email')" />
                <div v-if="userRole !== 'partner'">
                    <!-- Show selected banks as checkboxes -->
                    <div class="mt-2 text--secondary d-flex justify-space-between align-center">
                        <span v-if="localForm.banklist.length === 0">
                            {{ $t('broker.dialogs.selectBank') }}
                        </span>
                        <span v-else>
                            {{ $t('broker.dialogs.connectedBanks') }} - {{ $t('broker.dialogs.limit') }}
                        </span>
                    </div>

                    <div>
                        <div v-for="bank in localForm.banklist" :key="bank.name" class="d-flex align-center">
                            <v-checkbox v-model="localForm.banklist" :value="bank" return-object hide-details dense
                                class="mb-1 pa-2" />
                            <span>{{ bank.name }} — {{ bank.limit }}</span>
                        </div>
                    </div>

                    <div style="position: relative" class="mt-2">
                        <v-btn
                            v-if="banksCloseButton && $refs.bankSelect?.isMenuActive && localForm.banklist.length < maxBanks"
                            icon small style="position: absolute; top: -32px; right: 0; z-index: 2"
                            @click="banksCloseButton = false; $refs.bankSelect.blur()">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>

                        <v-expand-transition>
                            <v-select v-if="localShowBankSelect" data-testid="client-add-bank" v-model="localForm.banklist" :items="availableBanks"
                                :label="$t('broker.labels.addBank')" multiple chips deletable-chips return-object
                                item-text="name" :item-value="null" :item-disabled="isItemDisabled"
                                @focus="banksCloseButton = true" @change="enforceLimit" ref="bankSelect"
                                :error="localForm.banklist.length >= maxBanks" />
                        </v-expand-transition>
                        <div v-if="!showBankSelect && isMockDataOn" class="caption mt-1">
                            {{ $t('broker.dialogs.maxBanks') }}
                        </div>
                        <div v-if="!showBankSelect && !isMockDataOn" class="caption mt-1">
                            {{ $t('broker.dialogs.maxBanks2') }}
                        </div>
                    </div>
                </div>
                <!--link parter no broker-->
                <div v-if="userRole === 'broker' && !isEditingClient">
                    <v-checkbox v-model="localLinkCompanyToPartner" class="mt-4" hide-details>
                        <template v-slot:label>
                            <span :class="{ 'font-weight-bold': localCreateUser }">
                                Link Partner
                            </span>
                        </template>
                    </v-checkbox>
                    <div>
                        <v-text-field v-if="localLinkCompanyToPartner" v-model="localForm.partnerCnpj"
                            :disabled="isEditingClient" :label="$t('broker.labels.cnpj')"
                            placeholder="00.000.000/0000-00" @input="formatPartnerCnpj" @keypress="onlyNumbers"
                            maxlength="18" />
                    </div>
                </div>
                <div v-show="userRole === 'partner'" @click="handleFilePicker"
                    width="100" class="upload-files mr-2"
                    style="border-bottom: 1px solid white; line-height: 0.9rem; cursor: pointer;"
                    >
                    <v-divider class="my-5" />
                    <div class="my-2 py-3">
                        <v-messages data-testid="kyc-file-error" v-if="fileValidationError" :value="[fileValidationError]" color="error" class="mb-2" />
                        <h4 class="input-title-text upload-label">
                            <span
                                v-for="item in uploadLabelItems"
                                :key="item.key"
                                :class="item.className"
                            >
                                {{ item.text }}
                            </span>
                        </h4>
                    </div>
                </div>
                <v-checkbox v-if="!isEditingClient" v-model="localCreateUser" class="mt-4" hide-details disabled>
                    <template v-slot:label>
                        <span :class="{ 'font-weight-bold': localCreateUser }">
                            {{ $t('broker.dialogs.createUser') }}
                        </span>
                    </template>
                </v-checkbox>

                <!-- Comment option to create user for client already listed -->
                <!-- <div v-else-if="isEditingClient && !localForm.isUser" class="mt-4 d-flex align-center">
            <v-btn
              v-if="!localForm.pendingUser"
              color="primary"
              :loading="creatingUser"
              @click="createUserAndSendEmail()"
            >
              {{ $t('broker.dialogs.createUser') }}
            </v-btn>

            <v-chip
              v-else
              color="orange darken-2"
              text-color="white"
              class="ml-0"
            >
              {{ 'Pending User Confirmation' }}
            </v-chip>
          </div> -->
            </v-form>

            <div class="mt-4 d-flex justify-end">
                <v-btn text @click="handleCancel()">Cancel</v-btn>
                <v-btn :loading="localLoadingCreatingClient" :disabled="localLoadingCreatingClient 
                || !localForm.companyName 
                || !localForm.companyRepresentative
                || !(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localForm.email))
                || !localForm.cnpj
                || !!fileValidationError" 
                color="primary"
                    data-testid="save-client"
                    @click="handleSave">
                    {{ isEditingClient ? $t('instructions.save') : $t('instructions.add') }}
                </v-btn>
            </div>
            <!--input-->
            <input data-testid="kyc-file-input" @change="handleKYCUploadFiles" multiple type="file" ref="documentInput"
                style="display: none; padding:1rem !important" accept=".pdf" />
        </v-card>

    </v-dialog>
</template>
<script>
import { useSettingsStore } from '@/store';
import { MAX_KYC_FILE_SIZE } from '@/store'
import { bytesToDisplayMB } from '@/utils'
import { mapStores } from 'pinia';

export default {
    props: {
        value: Boolean,

        isEditingClient: Boolean,

        userRole: String,

        isBroker: Boolean,

        isMockDataOn: Boolean,

        loadingCreatingClient: Boolean,

        whiteLabelUrl: String,

        availableBanks: Array,

        showBankSelect: Boolean,

        maxBanks: Number,

        clientForm: Object,

        kycFiles: Array,

        createUser: Boolean,

        linkCompanyToPartner: Boolean,
    },
    data() {
        return {
            localForm: { ...this.clientForm },
            localCreateUser: false,
            localLinkCompanyToPartner: false,
            banksCloseButton: false,
            localShowBankSelect: false,
            localLoadingCreatingClient:false,
            localKycFiles: [],
            showFileSizeWarning: false
        }
    },
    watch: {
        clientForm: {
            immediate: true,
            deep: true,
            handler(value) {
                this.localForm = {
                    ...value,
                    banklist: value?.banklist
                        ? [...value.banklist]
                        : []
                }
            }
        },
        kycFiles: {
            immediate: true,
            deep: true,
            handler(value) {
                this.localKycFiles = Array.isArray(value) ? [...value] : []
            }
        },
        showBankSelect: {
            immediate: true,
            handler(v) {
                this.localShowBankSelect = v
            }
        },
        createUser: {
            immediate: true,
            handler(v) {
                this.localCreateUser = v
            }
        },

        linkCompanyToPartner: {
            immediate: true,
            handler(v) {
                this.localLinkCompanyToPartner = v
            }
        },
        loadingCreatingClient:{
            immediate: true,
            handler(v){
                this.localLoadingCreatingClient = v
            }
        }
    },
    computed: {
        showAddClientDialog: {
            get() {
                return this.value
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        ...mapStores(useSettingsStore),
        uploadLabelItems() {
            if (!this.localKycFiles.length) {
                return [{
                    key: 'empty',
                    text: this.$t('kyc.uploadLabel', { size: bytesToDisplayMB(MAX_KYC_FILE_SIZE) }),
                }]
            }

            return this.localKycFiles.map((file, index) => {
                const formattedName = this.settingsStore.formatFileName(file.name, 40)
                const size = (file.size / 1024 / 1024).toFixed(3)
                const rejected = file.size > MAX_KYC_FILE_SIZE

                return {
                    key: `${file.name}-${index}`,
                    text: `${formattedName} - ${size} MB`,
                    className: rejected ? 'error--text' : ''
                }
            })
        },
        fileValidationError() {
            if (!this.localKycFiles.length) return ''
            const oversized = this.localKycFiles.filter(f => f.size > MAX_KYC_FILE_SIZE)
            if (oversized.length) {
                return this.$t('kyc.errors.oversizeCount', { count: oversized.length, size: bytesToDisplayMB(MAX_KYC_FILE_SIZE) })
            }
            return ''
        },
    },
    methods: {
        resetForm() {
            this.localForm = {
                companyName: '',
                companyRepresentative: '',
                cnpj: '',
                email: '',
                banklist: [],
                partnerCnpj: ''
            }
        },
        handleSave() {
            this.$emit('save', {
                form: this.localForm,
                edit: this.isEditingClient,
                createUser: this.localCreateUser,
                linkCompanyToPartner: this.localLinkCompanyToPartner
            })

        },
        handleCancel() {
            this.$emit('cancel')
        },
        onlyNumbers(e) {
            if (!/[0-9]/.test(e.key)) e.preventDefault()
        },
        formatCNPJ() {
            let digits = this.localForm.cnpj.replace(/\D/g, '').slice(0, 14)

            if (digits.length <= 2) {
                this.localForm.cnpj = digits
            } else if (digits.length <= 5) {
                this.localForm.cnpj = `${digits.slice(0, 2)}.${digits.slice(2)}`
            } else if (digits.length <= 8) {
                this.localForm.cnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
            } else if (digits.length <= 12) {
                this.localForm.cnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
            } else {
                this.localForm.cnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
            }
        },
        formatPartnerCnpj() {
            let digits = this.localForm.partnerCnpj.replace(/\D/g, '').slice(0, 14)

            if (digits.length <= 2) {
                this.localForm.partnerCnpj = digits
            } else if (digits.length <= 5) {
                this.localForm.partnerCnpj = `${digits.slice(0, 2)}.${digits.slice(2)}`
            } else if (digits.length <= 8) {
                this.localForm.partnerCnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
            } else if (digits.length <= 12) {
                this.localForm.partnerCnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
            } else {
                this.localForm.partnerCnpj = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
            }
        },
        isItemDisabled(item) {
            const selected = this.localForm.banklist || []
            const isSelected = selected.some(s => s && item && s.id === item.id)
            return !isSelected && selected.length >= this.maxBanks
        },

        enforceLimit(val) {
            const n = Array.isArray(val) ? val.length : 0
            if (n > this.maxBanks) {
                this.localForm.banklist = val.slice(0, this.maxBanks)
                this.$nextTick(() => this.$refs.bankSelect && this.$refs.bankSelect.blur())
            }
            if (n >= this.maxBanks) {
                this.$nextTick(() => {
                    if (this.$refs.bankSelect?.isMenuActive) this.$refs.bankSelect.blur()
                    this.localShowBankSelect = false
                    this.banksCloseButton = false
                })
            }
        },
        handleFilePicker() {
            if (this.$refs.documentInput) {
                this.$refs.documentInput.click()
            }
        },
        handleKYCUploadFiles(event) {
            this.localKycFiles = [...event.target.files]
            const files = Array.from(event.target.files || [])
            this.$emit('upload-files', files)
            //event.target.value = null
        }
    }
}
</script>
<style scoped>
.upload-label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
</style>